import classNames from 'classnames';
import type { TrafficSituation as TrafficSituationModel } from '../models/trafficSituation';
import TrafficSituation, { TrafficSituationDisplay } from './TrafficSituation';
import styles from './TrafficSituationsModule.module.scss';
import usePagination from '@shared/hooks/usePagination';
import {
  CSSProperties,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import useElementSize from '@shared/hooks/useElementSize';
import { TRAFFIC_SITUATION_MINIMUM_NUMBER_OF_CHARACTERS } from '@shared/constants';
import { getRemPropertyPixelSize } from '@shared/utils/rem';
import PageIndicator from '@shared/components/PageIndicator';
import PaginatedItems from '@shared/components/PaginatedItems';
import { LayoutContext } from '@shared/contexts/LayoutContext';
import useVoicedTrafficSituations from '../hooks/useVoicedTrafficSituations';
import { DeepReadonly } from 'ts-essentials';
import { TrafficSituationContext } from '../models/trafficSituationContext';
import usePausableInterval from '@shared/hooks/usePausableInterval';

interface TrafficSituationsModuleProps {
  trafficSituations: DeepReadonly<TrafficSituationModel[]>;
  context: TrafficSituationContext;
  onCycleCompleted?: () => void;
}

export default function TrafficSituationsModule({
  trafficSituations,
  context,
  onCycleCompleted,
}: TrafficSituationsModuleProps) {
  useVoicedTrafficSituations(trafficSituations);
  const layout =
    useContext(LayoutContext) === 'horizontal' && context !== 'vehicle'
      ? 'vertical'
      : 'horizontal';

  const trafficSituationContainerRefs = useRef<Map<string, HTMLDivElement>>(
    new Map(),
  );
  const [viewTimes, setViewTimes] = useState<Map<string, number>>(new Map());
  const currentPagePaginationTime = useMemo(() => {
    if (viewTimes.size === 0) {
      return undefined;
    }

    // Use max view time for horizontal layout which do not use single expanded item logic
    if (layout === 'horizontal') {
      return Math.max(...viewTimes.values());
    }

    return [...viewTimes.values()].reduce((acc, value) => acc + value, 0); // Use total view time for vertical layout
  }, [layout, viewTimes]);
  const [preferredExpandedIndex, setPreferredExpandedIndex] =
    useState<number>(0);

  const trafficSituationsContainerRef = useRef<HTMLDivElement>(null);
  const trafficSituationsAvailableSize = useElementSize(
    trafficSituationsContainerRef,
    { boxSizing: 'content-box' },
  );

  function getAvailableSize(): number | undefined {
    if (layout === 'vertical') {
      const progressBarReservedHeight = getRemPropertyPixelSize(
        document.documentElement,
        '--traffic-situation-progress-bar-reserved-height',
      );
      return trafficSituationsAvailableSize.height !== undefined
        ? trafficSituationsAvailableSize.height - progressBarReservedHeight
        : undefined;
    }

    return trafficSituationsAvailableSize.width;
  }

  const availableSize = getAvailableSize();

  const {
    itemsForCurrentPage,
    currentPageIndex,
    pages,
    allItemsFitAtFullSizeForCurrentPage,
    nextPage,
    pageId,
  } = usePagination(
    availableSize,
    trafficSituations,
    trafficSituationContainerRefs.current,
    'id',
    {
      elementFlow: layout,
    },
  );

  const expandedIndex = useMemo(() => {
    const indexOfFirstWithDescription = itemsForCurrentPage.findIndex(
      (item) => item[0].description,
    );
    const firstValidIndex =
      indexOfFirstWithDescription === -1 ? 0 : indexOfFirstWithDescription;
    if (preferredExpandedIndex >= itemsForCurrentPage.length) {
      return firstValidIndex;
    }

    const indexOfNextWithDescription = itemsForCurrentPage.findIndex(
      (item, index) => item[0].description && index >= preferredExpandedIndex,
    );
    if (indexOfNextWithDescription === -1) {
      return firstValidIndex;
    }

    return indexOfNextWithDescription;
  }, [itemsForCurrentPage, preferredExpandedIndex]);

  const onViewTimeUpdated = useCallback(
    (trafficSituationId: string, newViewTimeInMilliseconds: number) => {
      setViewTimes((previousViewTimes) => {
        const newViewTimes = new Map(
          [...previousViewTimes.entries()].filter(([trafficSituationId]) =>
            itemsForCurrentPage.some(
              ([trafficSituation]) =>
                trafficSituation.id === trafficSituationId,
            ),
          ),
        );
        newViewTimes.set(trafficSituationId, newViewTimeInMilliseconds);
        return newViewTimes;
      });
    },
    [itemsForCurrentPage],
  );

  function getMaxItems(): number | undefined {
    if (
      layout === 'vertical' ||
      !trafficSituationsContainerRef.current ||
      availableSize === undefined
    ) {
      return undefined;
    }

    if (context === 'vehicle') {
      return 1;
    }

    const trafficSituationFontSize = getRemPropertyPixelSize(
      trafficSituationsContainerRef.current,
      '--Bread-font-size',
    );
    return Math.max(
      1,
      Math.ceil(
        availableSize /
          (trafficSituationFontSize *
            TRAFFIC_SITUATION_MINIMUM_NUMBER_OF_CHARACTERS),
      ),
    );
  }

  const goToNextPage = useCallback(() => {
    if (currentPageIndex >= pages - 1) {
      onCycleCompleted?.();
    }
    nextPage();
  }, [currentPageIndex, nextPage, onCycleCompleted, pages]);

  const onExpandedViewed = useCallback(() => {
    const hasMoreDescriptions = itemsForCurrentPage.some(
      (item, index) => item[0].description && index > expandedIndex,
    );

    if (!hasMoreDescriptions) {
      goToNextPage();
      setPreferredExpandedIndex(0);
      return;
    }

    setPreferredExpandedIndex(expandedIndex + 1);
  }, [expandedIndex, goToNextPage, itemsForCurrentPage]);

  const showItemsAsScrollable = useMemo(() => {
    return (
      layout === 'horizontal' ||
      allItemsFitAtFullSizeForCurrentPage ||
      itemsForCurrentPage.length <= 1
    );
  }, [allItemsFitAtFullSizeForCurrentPage, itemsForCurrentPage.length, layout]);

  // A single scrollable page has no page indicator timing its view
  usePausableInterval(
    onCycleCompleted,
    pages <= 1 && showItemsAsScrollable && itemsForCurrentPage.length > 0
      ? currentPagePaginationTime
      : undefined,
  );

  function getDisplay(itemIndex: number): TrafficSituationDisplay {
    if (showItemsAsScrollable) {
      return 'scrollable';
    }

    return expandedIndex === itemIndex ? 'expanded' : 'collapsed';
  }

  return (
    <div
      ref={trafficSituationsContainerRef}
      className={classNames(
        styles['traffic-situations-container'],
        styles[`traffic-situations-container--${layout}`],
        styles[`traffic-situations-container--${context}-context`],
        pages > 1 && styles['traffic-situations-container--paged'],
      )}
      style={
        {
          '--traffic-situations-max-items': getMaxItems(),
        } as CSSProperties
      }
    >
      <PaginatedItems
        component="div"
        itemIds={itemsForCurrentPage.map(([, paginationId]) => paginationId)}
        className={classNames(
          styles['traffic-situations'],
          styles[`traffic-situations--${layout}`],
          styles[`traffic-situations--${context}-context`],
        )}
        style={
          {
            '--available-width': `100%`,
            '--available-height': trafficSituationsAvailableSize
              ? `${trafficSituationsAvailableSize.height}px`
              : undefined,
          } as CSSProperties
        }
        aria-hidden="true"
      >
        {itemsForCurrentPage.map(
          ([trafficSituation, paginationId], itemIndex) => (
            <TrafficSituation
              key={paginationId}
              paginationId={paginationId}
              trafficSituation={trafficSituation}
              display={getDisplay(itemIndex)}
              context={context}
              paginated={pages > 1}
              viewTimeUpdated={onViewTimeUpdated}
              onExpandedViewed={onExpandedViewed}
            ></TrafficSituation>
          ),
        )}
      </PaginatedItems>
      <div className={styles['traffic-situations-container__page-indicator']}>
        {pageId && pages > 1 && (
          <PageIndicator
            pageId={pageId}
            currentPagePaginationTime={
              showItemsAsScrollable ? currentPagePaginationTime : undefined
            }
            currentPageIndex={currentPageIndex}
            pages={pages}
            onPaging={goToNextPage}
            theme={context === 'vehicle' ? 'light' : undefined}
          />
        )}
      </div>
      <div
        className={classNames(
          styles['all-traffic-situations'],
          styles[`all-traffic-situations--${layout}`],
          styles[`all-traffic-situations--${context}-context`],
        )}
        style={
          {
            '--available-width': trafficSituationsAvailableSize
              ? `${trafficSituationsAvailableSize.width}px`
              : undefined,
            '--available-height': trafficSituationsAvailableSize
              ? `${trafficSituationsAvailableSize.height}px`
              : undefined,
          } as CSSProperties
        }
        aria-live="polite"
      >
        {trafficSituations.map((trafficSituation) => (
          <TrafficSituation
            ref={(trafficSituationContainerElement) => {
              if (trafficSituationContainerElement) {
                trafficSituationContainerRefs.current.set(
                  trafficSituation.id,
                  trafficSituationContainerElement,
                );
              } else {
                trafficSituationContainerRefs.current.delete(
                  trafficSituation.id,
                );
              }
            }}
            key={trafficSituation.id}
            trafficSituation={trafficSituation}
            display="pagination-measuring"
            context={context}
            paginated={pages > 1}
          ></TrafficSituation>
        ))}
      </div>
    </div>
  );
}
